/**
 * The shared pieces of the "idiot-proof forms" work (product owner, 7 Oct),
 * used by every HelpDesk form:
 *
 *   - `useSubmitLabel`: the primary button says how much is left —
 *     "Submit · 2 still needed" — instead of being disabled without a word.
 *   - `useFormAnnouncer` / `FormAnnouncer`: a polite live region that says
 *     "2 fields still needed" when Submit is pressed too early (focus moves
 *     to the first one at the same time, see `lib/reveal-first-problem.ts`).
 *   - `useFormCheckMessage`: the friendly line under a field that failed a
 *     plain check (`lib/form-checks.ts`).
 *   - `FormReview`: "Here's what IT will get", the summary the longer forms
 *     show before the final send — the same look as the access wizard's
 *     "Check and send" page.
 */

import { Button, Typography } from "@norican/react";
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";

import { useT } from "@/i18n";
import { type FormCheckKey, MIN_TEXT_LENGTH } from "@/lib/form-checks";

/**
 * Added to a form while its summary shows: every direct child is hidden
 * except those marked `data-review-keep` (the summary, the error, the live
 * region). Hidden, not unmounted — uploads and answers live in the fields.
 */
export const REVIEW_HIDES_FORM = "[&>*:not([data-review-keep])]:hidden";

/** The primary button's label, with the count of fields still needed when there are any. */
export function useSubmitLabel() {
  const { t } = useT("common");
  return useCallback(
    (label: string, stillNeeded: number): string =>
      stillNeeded > 0 ? t("formCheck.submitCount", { label, count: stillNeeded }) : label,
    [t],
  );
}

/** The friendly line under a field that failed a plain check. */
export function useFormCheckMessage() {
  const { t } = useT("common");
  return useCallback(
    (key: FormCheckKey | null | undefined, min: number = MIN_TEXT_LENGTH): string | undefined => {
      switch (key) {
        case null:
        case undefined:
          return undefined;
        case "pastDate":
          return t("formCheck.problem.pastDate");
        case "notPicked":
          return t("formCheck.problem.notPicked");
        case "email":
          return t("formCheck.problem.email");
        case "tooShort":
          return t("formCheck.problem.tooShort", { min });
      }
    },
    [t],
  );
}

/**
 * What a blocked Submit says out loud. `announce` clears the region first, so
 * pressing Submit twice with the same gap is read out twice.
 */
export function useFormAnnouncer() {
  const { t } = useT("common");
  const [message, setMessage] = useState("");
  const timer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
      }
    },
    [],
  );
  const announce = useCallback(
    (stillNeeded: number, toFix: number) => {
      const next =
        stillNeeded > 0
          ? t("formCheck.announce.stillNeeded", { count: stillNeeded })
          : toFix > 0
            ? t("formCheck.announce.toFix", { count: toFix })
            : "";
      setMessage("");
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
      }
      timer.current = window.setTimeout(() => setMessage(next), 60);
    },
    [t],
  );
  return { message, announce };
}

/** The live region `useFormAnnouncer` writes to; visually hidden. */
export function FormAnnouncer({ message }: { message: string }) {
  return (
    <div role="status" aria-live="polite" className="sr-only" data-review-keep>
      {message}
    </div>
  );
}

/** One answered question on the summary. */
export interface FormReviewRow {
  key: string;
  label: ReactNode;
  value: ReactNode;
  /** Back to the form, on this field. Absent: no Edit button on the row. */
  onEdit?: () => void;
}

export interface FormReviewProps {
  rows: FormReviewRow[];
  /** Back to the form without changing anything. */
  onBack: () => void;
  onSend: () => void;
  sending?: boolean;
  /** The send button's label; "Send" unless the form says otherwise. */
  sendLabel?: string;
  /** Shown above the buttons: a failed send, the "filing as" line. */
  footer?: ReactNode;
}

/**
 * "Here's what IT will get": every answered question with its answer, an Edit
 * per row that goes back to that field, then Send. The look is the access
 * wizard's "Check and send" (a bordered box of label → answer pairs).
 */
export function FormReview({
  rows,
  onBack,
  onSend,
  sending = false,
  sendLabel,
  footer,
}: FormReviewProps) {
  const { t } = useT("common");
  const headingId = useId();
  const headingRef = useRef<HTMLElement>(null);

  // The summary replaces the form in place: take the person (and their
  // screen reader) to its heading, or they are left at the bottom of the page.
  useEffect(() => {
    const heading = headingRef.current;
    if (heading === null) {
      return;
    }
    if (typeof heading.scrollIntoView === "function") {
      heading.scrollIntoView({ block: "start" });
    }
    heading.focus({ preventScroll: true });
  }, []);

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4" data-review-keep>
      <div className="flex flex-col gap-1">
        <Typography
          as="h3"
          variant="heading-5"
          id={headingId}
          ref={headingRef}
          tabIndex={-1}
          className="outline-none"
        >
          {t("formCheck.review.title")}
        </Typography>
        <Typography variant="body-sm" tone="muted">
          {t("formCheck.review.intro")}
        </Typography>
      </div>

      <dl className="border-border-subtle m-0 flex flex-col rounded-md border">
        {rows.map((row) => (
          <div
            key={row.key}
            className="border-border-subtle grid gap-x-3 gap-y-1 border-b p-3 last:border-b-0 sm:grid-cols-[minmax(0,14rem)_1fr_auto] sm:items-start"
          >
            <dt className="text-fg-muted text-sm">{row.label}</dt>
            <dd className="m-0 text-sm break-words whitespace-pre-line">{row.value}</dd>
            <div className="sm:justify-self-end">
              {row.onEdit === undefined ? null : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={row.onEdit}
                  disabled={sending}
                  aria-label={
                    typeof row.label === "string"
                      ? t("formCheck.review.editField", { field: row.label })
                      : undefined
                  }
                >
                  {t("formCheck.review.edit")}
                </Button>
              )}
            </div>
          </div>
        ))}
      </dl>

      {footer}

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button type="button" variant="ghost" tone="neutral" onClick={onBack} disabled={sending}>
          {t("formCheck.review.back")}
        </Button>
        <Button type="button" onClick={onSend} loading={sending} disabled={sending}>
          {sendLabel ?? t("formCheck.review.send")}
        </Button>
      </div>
    </section>
  );
}
